import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CreditCard, Building2, Loader2 } from 'lucide-react';
import axios from 'axios';
import { useCart } from '../context/CartContext';
import { ShimmerButton } from '../components/magicui/shimmer-button';
import { PageIntro } from '../components/PageIntro';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from '../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Checkout = () => {
  const { cartItems, getCartTotal, clearCart } = useCart();
  const navigate = useNavigate();
  const [paymentMethod, setPaymentMethod] = useState('bank');
  const [orderNumber, setOrderNumber] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    zipCode: '',
    notes: ''
  });

  useEffect(() => {
    if (cartItems.length === 0 && !orderNumber) {
      navigate('/cart', { replace: true });
    }
  }, [cartItems.length, orderNumber, navigate]);

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      
      // Generate order number
      const newOrderNumber = 'ORD-' + Date.now();
      
      // Prepare order data
      const orderData = {
        orderNumber: newOrderNumber,
        customerInfo: {
          firstName: formData.firstName,
          lastName: formData.lastName,
          email: formData.email,
          phone: formData.phone,
          address: formData.address,
          city: formData.city,
          zipCode: formData.zipCode,
          notes: formData.notes
        },
        items: cartItems.map(item => ({
          productId: item.id,
          name: item.name,
          price: item.price,
          quantity: item.quantity
        })),
        total: getCartTotal(),
        paymentMethod
      };
      
      // Send order to backend
      const response = await axios.post(`${API}/orders`, orderData);
      
      if (response.data.success) {
        sessionStorage.setItem(`order-token:${newOrderNumber}`, response.data.accessToken);
        // If payment method is card, redirect to Stripe
        if (paymentMethod === 'card') {
          try {
            const checkoutData = {
              order_number: newOrderNumber,
              currency: 'eur',
              origin_url: window.location.origin,
              metadata: {
                customer_email: formData.email,
                customer_name: `${formData.firstName} ${formData.lastName}`
              }
            };
            
            const stripeResponse = await axios.post(`${API}/payments/create-checkout-session`, checkoutData, { headers: { 'X-Order-Token': response.data.accessToken } });
            
            if (stripeResponse.data.url) {
              // Redirect to Stripe checkout
              window.location.href = stripeResponse.data.url;
            }
          } catch (stripeError) {
            console.error('Stripe error:', stripeError);
            toast({
              title: 'Greška plaćanja',
              description: 'Došlo je do greške pri obradi plaćanja. Pokušajte ponovo.',
              variant: 'destructive'
            });
          }
        } else {
          // For bank transfer, show success
          setOrderNumber(newOrderNumber);
          clearCart();
          navigate(`/narudzba/${encodeURIComponent(newOrderNumber)}`);
          toast({
            title: 'Narudžba uspješno kreirana',
            description: `Broj narudžbe: ${newOrderNumber}`,
          });
        }
      }
    } catch (error) {
      console.error('Error submitting order:', error);
      toast({
        title: 'Greška',
        description: 'Došlo je do greške pri kreiranju narudžbe. Pokušajte ponovo.',
        variant: 'destructive'
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (cartItems.length === 0 && !orderNumber) return null;

  if (orderNumber) return null;

  return (
    <div className="premium-page premium-form">
      <div className="premium-container">
        <PageIntro title="Još korak do vašeg svjetla." testId="checkout-title" eyebrow="MAXLIGHT / Naplata" />

        <form onSubmit={handleSubmitOrder} data-testid="checkout-form">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Checkout Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Customer Information */}
              <Card className="premium-panel border-t border-gray-300 pt-6">
                <CardHeader className="px-0 pt-0 pb-6">
                  <CardTitle data-testid="checkout-delivery-heading">Podaci za dostavu</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4 px-0">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="firstName">Ime *</Label>
                      <Input
                        id="firstName"
                        data-testid="checkout-first-name-input"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="lastName">Prezime *</Label>
                      <Input
                        id="lastName"
                        data-testid="checkout-last-name-input"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        data-testid="checkout-email-input"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Telefon *</Label>
                      <Input
                        id="phone"
                        data-testid="checkout-phone-input"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="address">Adresa *</Label>
                    <Input
                      id="address"
                      data-testid="checkout-address-input"
                      name="address"
                      value={formData.address}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="city">Grad *</Label>
                      <Input
                        id="city"
                        data-testid="checkout-city-input"
                        name="city"
                        value={formData.city}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="zipCode">Poštanski broj *</Label>
                      <Input
                        id="zipCode"
                        data-testid="checkout-zip-code-input"
                        name="zipCode"
                        value={formData.zipCode}
                        onChange={handleInputChange}
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="notes">Napomena (opcionalno)</Label>
                    <textarea
                      id="notes"
                      data-testid="checkout-notes-input"
                      name="notes"
                      value={formData.notes}
                      onChange={handleInputChange}
                      rows="3"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-gray-900"
                    />
                  </div>
                </CardContent>
              </Card>

              {/* Payment Method */}
              <Card className="premium-panel border-t border-gray-300 pt-6">
                <CardHeader className="px-0 pt-0 pb-6">
                  <CardTitle data-testid="checkout-payment-heading">Način plaćanja</CardTitle>
                </CardHeader>
                <CardContent className="px-0">
                  <Tabs value={paymentMethod} onValueChange={setPaymentMethod}>
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="bank" className="flex items-center space-x-2" data-testid="checkout-payment-bank">
                        <Building2 className="w-4 h-4" />
                        <span>Bankovni račun</span>
                      </TabsTrigger>
                      <TabsTrigger value="card" className="flex items-center space-x-2" data-testid="checkout-payment-card">
                        <CreditCard className="w-4 h-4" />
                        <span>Kartica</span>
                      </TabsTrigger>
                    </TabsList>
                    
                    <TabsContent value="bank" className="mt-4" data-testid="checkout-bank-details">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700">
                          Podaci i kodovi za uplatu prikazat će se nakon narudžbe.
                          E-mail potvrdu šaljemo nakon što evidentiramo primljenu uplatu.
                        </p>
                      </div>
                    </TabsContent>
                    
                    <TabsContent value="card" className="mt-4" data-testid="checkout-card-details">
                      <div className="bg-gray-50 p-4 rounded-lg">
                        <p className="text-sm text-gray-700 mb-4">
                          Siguran online način plaćanja putem Stripe platforme. Prihvaćamo sve glavne kartice (Visa, Mastercard, American Express).
                        </p>
                        <p className="text-xs text-gray-600">
                          Nakon potvrde narudžbe bit ćete preusmjereni na sigurnu Stripe stranicu za unos podataka o kartici.
                        </p>
                      </div>
                    </TabsContent>
                  </Tabs>
                </CardContent>
              </Card>
            </div>

            {/* Order Summary */}
            <div className="lg:col-span-1">
              <Card className="premium-summary lg:sticky lg:top-28 !p-0 shadow-none">
                <CardHeader>
                  <CardTitle data-testid="checkout-summary-heading">Vaša narudžba</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="space-y-3">
                    {cartItems.map((item) => (
                      <div key={item.id} className="flex justify-between gap-3 text-xs">
                        <span className="text-gray-600 min-w-0 break-words" data-testid={`checkout-item-name-${item.id}`}>
                          {item.name} x {item.quantity}
                        </span>
                        <span className="font-semibold shrink-0" data-testid={`checkout-item-total-${item.id}`}>
                          {(item.price * item.quantity).toFixed(2)} €
                        </span>
                      </div>
                    ))}
                  </div>

                  <div className="border-t pt-4">
                    <div className="flex justify-between items-center">
                      <span className="text-xl font-semibold">Ukupno:</span>
                      <span className="text-2xl font-bold" data-testid="checkout-total">
                        {getCartTotal().toFixed(2)} €
                      </span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 mb-4" data-testid="checkout-vat-notice">Iznos uključuje 25 % PDV-a.</p>
                  <ShimmerButton type="submit" className="w-full" disabled={submitting} data-testid="checkout-submit-button">
                    {submitting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Obrada...
                      </>
                    ) : (
                      'Potvrdi narudžbu'
                    )}
                  </ShimmerButton>
                </CardContent>
              </Card>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Checkout;
